/**
 * Shared helpers consumed by every list-style implementation.
 *
 * Kept deliberately tiny — no React, no hooks, just plain data.
 * Pulling these into one file means each of the three <Style*> components
 * uses the exact same type-palette + tree-walk logic, so the only thing
 * that varies between them is visual presentation.
 */
import type { IvrNodeRow } from '@/api/ivrNodes';
import type { IvrEdgeRow } from '@/api/ivrEdges';

// ── Type-accent palette ──────────────────────────────────────────────────────
//
// Mirrors the table in NodeRow.tsx. We re-derive here instead of importing
// the private constant so each style can riff on its own colour treatment
// (Blueprint wants monochrome, Timeline wants solid dots, Cards wants
// tinted fills) without editing the canonical row component.

export interface TypeAccent {
  /** Solid colour (bg-*-400). Used for rails and dots. */
  solid: string;
  /** Tinted fill (bg-*-50). Used for card backgrounds. */
  tint: string;
  /** Foreground text on the tint. */
  fg: string;
  /** Border for tinted containers. */
  border: string;
  /** Hex-ish token name — used as the Tailwind colour family stem. */
  hue: 'indigo' | 'sky' | 'cyan' | 'amber' | 'violet' | 'emerald' | 'fuchsia' | 'rose' | 'slate' | 'teal';
  /** Single uppercase letter used by the Blueprint style. */
  abbr: string;
}

const PALETTE: Record<string, TypeAccent> = {
  menu:               { solid: 'bg-indigo-500',  tint: 'bg-indigo-50',  fg: 'text-indigo-700',  border: 'border-indigo-200',  hue: 'indigo',  abbr: 'M' },
  playback_settings:  { solid: 'bg-indigo-500',  tint: 'bg-indigo-50',  fg: 'text-indigo-700',  border: 'border-indigo-200',  hue: 'indigo',  abbr: 'M' },
  playback:           { solid: 'bg-sky-500',     tint: 'bg-sky-50',     fg: 'text-sky-700',     border: 'border-sky-200',     hue: 'sky',     abbr: 'P' },
  bulletin:           { solid: 'bg-sky-500',     tint: 'bg-sky-50',     fg: 'text-sky-700',     border: 'border-sky-200',     hue: 'sky',     abbr: 'B' },
  series_folder:      { solid: 'bg-cyan-500',    tint: 'bg-cyan-50',    fg: 'text-cyan-700',    border: 'border-cyan-200',    hue: 'cyan',    abbr: 'F' },
  latest_n_folder:    { solid: 'bg-cyan-500',    tint: 'bg-cyan-50',    fg: 'text-cyan-700',    border: 'border-cyan-200',    hue: 'cyan',    abbr: 'F' },
  rotation_folder:    { solid: 'bg-cyan-500',    tint: 'bg-cyan-50',    fg: 'text-cyan-700',    border: 'border-cyan-200',    hue: 'cyan',    abbr: 'F' },
  random_folder:      { solid: 'bg-cyan-500',    tint: 'bg-cyan-50',    fg: 'text-cyan-700',    border: 'border-cyan-200',    hue: 'cyan',    abbr: 'F' },
  voicemail:          { solid: 'bg-amber-500',   tint: 'bg-amber-50',   fg: 'text-amber-700',   border: 'border-amber-200',   hue: 'amber',   abbr: 'V' },
  listen_voicemail:   { solid: 'bg-amber-500',   tint: 'bg-amber-50',   fg: 'text-amber-700',   border: 'border-amber-200',   hue: 'amber',   abbr: 'V' },
  conference:         { solid: 'bg-violet-500',  tint: 'bg-violet-50',  fg: 'text-violet-700',  border: 'border-violet-200',  hue: 'violet',  abbr: 'C' },
  payment:            { solid: 'bg-emerald-600', tint: 'bg-emerald-50', fg: 'text-emerald-700', border: 'border-emerald-200', hue: 'emerald', abbr: '$' },
  shopping:           { solid: 'bg-emerald-600', tint: 'bg-emerald-50', fg: 'text-emerald-700', border: 'border-emerald-200', hue: 'emerald', abbr: '$' },
  quiz:               { solid: 'bg-fuchsia-500', tint: 'bg-fuchsia-50', fg: 'text-fuchsia-700', border: 'border-fuchsia-200', hue: 'fuchsia', abbr: 'Q' },
  survey:             { solid: 'bg-fuchsia-500', tint: 'bg-fuchsia-50', fg: 'text-fuchsia-700', border: 'border-fuchsia-200', hue: 'fuchsia', abbr: 'S' },
  transfer:           { solid: 'bg-rose-500',    tint: 'bg-rose-50',    fg: 'text-rose-700',    border: 'border-rose-200',    hue: 'rose',    abbr: 'T' },
  dial:               { solid: 'bg-rose-500',    tint: 'bg-rose-50',    fg: 'text-rose-700',    border: 'border-rose-200',    hue: 'rose',    abbr: 'D' },
  ring_group:         { solid: 'bg-rose-500',    tint: 'bg-rose-50',    fg: 'text-rose-700',    border: 'border-rose-200',    hue: 'rose',    abbr: 'R' },
  sip_connect:        { solid: 'bg-rose-500',    tint: 'bg-rose-50',    fg: 'text-rose-700',    border: 'border-rose-200',    hue: 'rose',    abbr: 'S' },
  extension:          { solid: 'bg-rose-500',    tint: 'bg-rose-50',    fg: 'text-rose-700',    border: 'border-rose-200',    hue: 'rose',    abbr: 'E' },
  jump:               { solid: 'bg-slate-500',   tint: 'bg-slate-50',   fg: 'text-slate-700',   border: 'border-slate-200',   hue: 'slate',   abbr: 'J' },
  back:               { solid: 'bg-slate-500',   tint: 'bg-slate-50',   fg: 'text-slate-700',   border: 'border-slate-200',   hue: 'slate',   abbr: '<' },
  repeat:             { solid: 'bg-slate-500',   tint: 'bg-slate-50',   fg: 'text-slate-700',   border: 'border-slate-200',   hue: 'slate',   abbr: 'R' },
  return_to_bookmark: { solid: 'bg-teal-500',    tint: 'bg-teal-50',    fg: 'text-teal-700',    border: 'border-teal-200',    hue: 'teal',    abbr: 'B' },
  bookmark:           { solid: 'bg-teal-500',    tint: 'bg-teal-50',    fg: 'text-teal-700',    border: 'border-teal-200',    hue: 'teal',    abbr: 'B' },
};

export function accentFor(type: string): TypeAccent {
  return PALETTE[type] ?? {
    solid: 'bg-slate-400',
    tint: 'bg-slate-50',
    fg: 'text-slate-600',
    border: 'border-slate-200',
    hue: 'slate',
    abbr: '?',
  };
}

// ── Shared types ─────────────────────────────────────────────────────────────

export interface AdjacencyMap { [fromNodeId: string]: IvrEdgeRow[] }

export interface StyleProps {
  /** Tree id — required by AddChildPopover. */
  treeId: string;
  nodes: ReadonlyArray<IvrNodeRow>;
  edges: ReadonlyArray<IvrEdgeRow>;
  rootNodeId: string | null | undefined;
  canMutate: boolean;
  /** Pre-computed adjacency so each style renders without re-walking edges. */
  adj: AdjacencyMap;
  nodeMap: Record<string, IvrNodeRow>;
  /** `null` = no search filter; otherwise a set of ids to include. */
  matchIds: Set<string> | null;
  /** Expansion set — ids currently expanded. */
  expanded: Set<string>;
  /** Flash highlight (single node id) — used by jump-to-node. */
  flashNodeId: string | null;
  /** Row-ref registrar so jump-to-node can scroll. */
  registerRowRef: (nodeId: string, el: HTMLDivElement | null) => void;
  /** Orphan nodes (unreachable from root). */
  orphans: ReadonlyArray<IvrNodeRow>;
  // ── Handlers ─────────────────────────────────────────────────────────────
  onToggle: (nodeId: string) => void;
  onEditNode: (node: IvrNodeRow) => void;
  onDeleteNode?: (node: IvrNodeRow) => void;
  onAddChild: (node: IvrNodeRow, anchor: DOMRect) => void;
  onJumpToNode: (nodeId: string) => void;
}

// ── Branch-key classification ────────────────────────────────────────────────

export type BranchKeyFlavor = 'dtmf' | 'special' | 'next' | 'root' | 'orphan';

export function flavorForBranchKey(k: string | null, isRoot: boolean, isOrphan: boolean): BranchKeyFlavor {
  if (isRoot) return 'root';
  if (isOrphan || !k) return 'orphan';
  if (['timeout', 'fallback', 'invalid', 'default'].includes(k)) return 'special';
  if (k === 'next') return 'next';
  return 'dtmf';
}

// ── Branch-key friendly labels ───────────────────────────────────────────────
//
// Plain-English description for each well-known branch_key. Used as a
// `title` tooltip on the branch-key pill so admins building the tree
// can hover any chip and see what the edge represents — no need to
// remember whether `posted` means "post succeeded" or "post in
// progress". Add new entries here when handlers introduce new edges.
//
// Unknown keys (raw DTMF digits, custom strings) just fall back to the
// raw key as the title.

export const BRANCH_KEY_LABELS: Readonly<Record<string, string>> = {
  // Generic outcome keys
  next:         'Next — natural completion',
  success:      'Success',
  failure:      'Failure',
  done:         'Finished',
  empty:        'Nothing to play',
  saved:        'Saved',
  enrolled:     'Enrolled successfully',
  already:      'Already enrolled',
  full:         'Capacity reached',
  passed:       'Passed',
  failed:       'Did not pass',
  no_course:    'No course configured',
  unavailable:  'Resource unavailable',

  // public_forum (single-node menu, May 2026)
  posted:       'Caller posted a message (1 → record)',
  aborted:      'Recording / save setup failed',
  no_caller:    'Caller path requires a caller profile that isn’t bound',

  // Menu fall-through keys (also used by public_forum menu)
  timeout:      'Caller didn’t press in time',
  fallback:     'Used when no other edge matches',
  invalid:      'Caller pressed an unrecognised key',
  default:      'Catch-all fallback for any key',
};

/**
 * Friendly tooltip text for a branch_key. Returns the raw key when no
 * entry exists so unknown keys still render a sensible tooltip.
 */
export function describeBranchKey(k: string | null | undefined): string {
  if (!k) return '';
  return BRANCH_KEY_LABELS[k] ?? k;
}
